import { useState, useMemo, useEffect } from "react";
import { FaSearch, FaSort } from "react-icons/fa";
import BrowseSongsSong from "./BrowseSongsSong";

// Helper function to check if a string starts with a letter (including Norwegian characters)
const startsWithLetter = (str) => {
    if (!str || str.length === 0) return false;
    const firstChar = str.charAt(0);
    return /[a-zA-ZæøåÆØÅ]/.test(firstChar);
};

// Sort function for songs
const sortSongs = (songsToSort, sortBy) => {
    const sorted = [...songsToSort];

    if (sortBy === "alphabetical") {
        sorted.sort((a, b) => {
            const aTitle = a.title || "";
            const bTitle = b.title || "";
            const aStartsWithLetter = startsWithLetter(aTitle);
            const bStartsWithLetter = startsWithLetter(bTitle);

            // If both start with letters, use Norwegian locale comparison
            if (aStartsWithLetter && bStartsWithLetter) {
                return aTitle.localeCompare(bTitle, "no", {
                    sensitivity: "base",
                    caseFirst: "lower",
                });
            }

            // If one starts with letter and other doesn't, letter comes first
            if (aStartsWithLetter && !bStartsWithLetter) return -1;
            if (!aStartsWithLetter && bStartsWithLetter) return 1;

            // If neither starts with letter, compare normally with Norwegian locale
            return aTitle.localeCompare(bTitle, "no", {
                sensitivity: "base",
                caseFirst: "lower",
            });
        });
    } else {
        // Sort by popularity (usageCount descending), then alphabetically by title
        sorted.sort((a, b) => {
            const aCount = a.usageCount || 0;
            const bCount = b.usageCount || 0;

            // First sort by usageCount (descending)
            if (aCount !== bCount) {
                return bCount - aCount;
            }

            // If usageCount is the same, sort alphabetically by title with Norwegian locale
            const aTitle = a.title || "";
            const bTitle = b.title || "";
            const aStartsWithLetter = startsWithLetter(aTitle);
            const bStartsWithLetter = startsWithLetter(bTitle);

            // If both start with letters, use Norwegian locale comparison
            if (aStartsWithLetter && bStartsWithLetter) {
                return aTitle.localeCompare(bTitle, "no", {
                    sensitivity: "base",
                    caseFirst: "lower",
                });
            }

            // If one starts with letter and other doesn't, letter comes first
            if (aStartsWithLetter && !bStartsWithLetter) return -1;
            if (!aStartsWithLetter && bStartsWithLetter) return 1;

            // If neither starts with letter, compare normally with Norwegian locale
            return aTitle.localeCompare(bTitle, "no", {
                sensitivity: "base",
                caseFirst: "lower",
            });
        });
    }

    return sorted;
};

const BrowseSongs = ({ newSongs, setNewSongs, allSongs }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [sortType, setSortType] = useState("popular"); // "popular" or "alphabetical"
    const [showSortDropdown, setShowSortDropdown] = useState(false);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                showSortDropdown &&
                !event.target.closest("[data-sort-dropdown]")
            ) {
                setShowSortDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showSortDropdown]);

    const filteredAndSortedSongs = useMemo(() => {
        let filtered = allSongs;

        // Apply search filter
        if (searchTerm && searchTerm.trim().length > 0) {
            const search = searchTerm.trim().toLowerCase();
            filtered = allSongs.filter(
                (song) =>
                    song.title.toLowerCase().includes(search) ||
                    song.artist.toLowerCase().includes(search)
            );
        }

        // Apply sorting
        return sortSongs(filtered, sortType);
    }, [allSongs, searchTerm, sortType]);

    return (
        <div
            className="browse-songs-container"
            style={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                overflow: "hidden",
                flex: 1,
                minHeight: 0,
            }}
        >
            <div
                style={{
                    flexShrink: 0,
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "0 1vh",
                }}
            >
                <div
                    style={{
                        color: "white",
                        width: "100%",
                        textAlign: "left",
                        marginBottom: "0.5vh",
                        marginTop: "1vh",
                        fontSize: 14,
                        fontWeight: 600,
                    }}
                >
                    Velg blant{" "}
                    {searchTerm
                        ? `${filteredAndSortedSongs.length} av ${allSongs.length} sanger`
                        : `${allSongs.length} sanger`}
                </div>
                {/* <div
                    style={{
                        color: "#9ca3af",
                        textAlign: "left",
                        marginBottom: "0.8vh",
                        fontSize: 12,
                    }}
                >
                    {searchTerm
                        ? `${filteredAndSortedSongs.length} av ${allSongs.length} sanger`
                        : `${allSongs.length} sanger`}
                </div> */}
                <div
                    style={{
                        display: "flex",
                        gap: "1vh",
                        width: "100%",
                        marginBottom: "1vh",
                    }}
                >
                    <div
                        style={{
                            position: "relative",
                            flex: 1,
                        }}
                    >
                        <FaSearch
                            style={{
                                position: "absolute",
                                left: "12px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: "#9ca3af",
                                fontSize: 14,
                                pointerEvents: "none",
                            }}
                        />
                        <input
                            type="text"
                            placeholder="Søk etter sang eller artist"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: "100%",
                                padding: "10px 10px 10px 35px",
                                margin: 0,
                                borderRadius: "0.5vh",
                                border: "1px solid #2a3854",
                                backgroundColor: "#2a3854",
                                color: "white",
                                boxSizing: "border-box",
                            }}
                        />
                    </div>
                    <div
                        style={{
                            position: "relative",
                        }}
                        data-sort-dropdown
                    >
                        <button
                            onClick={() =>
                                setShowSortDropdown(!showSortDropdown)
                            }
                            style={{
                                height: "100%",
                                minWidth: "3vh",
                                padding: "0 1vh",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "0.5vh",
                                backgroundColor: "#2a3854",
                                border: "1px solid #2a3854",
                                borderRadius: "0.5vh",
                                color: "white",
                                cursor: "pointer",
                                fontSize: 14,
                                transition: "background-color 0.2s",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                    "#3a4a64";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                    "#2a3854";
                            }}
                        >
                            <FaSort style={{ fontSize: 12 }} />
                        </button>
                        {showSortDropdown && (
                            <div
                                style={{
                                    position: "absolute",
                                    top: "100%",
                                    right: 0,
                                    marginTop: "0.5vh",
                                    backgroundColor: "#2a3854",
                                    border: "1px solid #3a4a64",
                                    borderRadius: "0.5vh",
                                    minWidth: "180px",
                                    zIndex: 1000,
                                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
                                }}
                            >
                                <button
                                    onClick={() => {
                                        setSortType("popular");
                                        setShowSortDropdown(false);
                                    }}
                                    style={{
                                        width: "100%",
                                        padding: "1vh",
                                        textAlign: "left",
                                        backgroundColor:
                                            sortType === "popular"
                                                ? "#3a4a64"
                                                : "transparent",
                                        border: "none",
                                        color: "white",
                                        cursor: "pointer",
                                        fontSize: 14,
                                        borderTopLeftRadius: "0.5vh",
                                        borderTopRightRadius: "0.5vh",
                                    }}
                                    onMouseEnter={(e) => {
                                        if (sortType !== "popular") {
                                            e.currentTarget.style.backgroundColor =
                                                "#3a4a64";
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (sortType !== "popular") {
                                            e.currentTarget.style.backgroundColor =
                                                "transparent";
                                        }
                                    }}
                                >
                                    Mest populære
                                </button>
                                <button
                                    onClick={() => {
                                        setSortType("alphabetical");
                                        setShowSortDropdown(false);
                                    }}
                                    style={{
                                        width: "100%",
                                        padding: "1vh",
                                        textAlign: "left",
                                        backgroundColor:
                                            sortType === "alphabetical"
                                                ? "#3a4a64"
                                                : "transparent",
                                        border: "none",
                                        color: "white",
                                        cursor: "pointer",
                                        fontSize: 14,
                                        borderBottomLeftRadius: "0.5vh",
                                        borderBottomRightRadius: "0.5vh",
                                    }}
                                    onMouseEnter={(e) => {
                                        if (sortType !== "alphabetical") {
                                            e.currentTarget.style.backgroundColor =
                                                "#3a4a64";
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (sortType !== "alphabetical") {
                                            e.currentTarget.style.backgroundColor =
                                                "transparent";
                                        }
                                    }}
                                >
                                    Alfabetisk
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div
                className="browse-songs-list"
                style={{
                    flex: 1,
                    width: "100%",
                    overflowY: "auto",
                    overflowX: "hidden",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "center",
                }}
            >
                {filteredAndSortedSongs.map((song, i) => (
                    <BrowseSongsSong
                        newSongs={newSongs}
                        setNewSongs={setNewSongs}
                        key={song.title + i}
                        song={song}
                    />
                ))}
            </div>
        </div>
    );
};

export default BrowseSongs;
